import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';
import { useSelector } from 'react-redux';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';

// Lodash
import omit from 'lodash/omit';

// Misc / utils
import ValueConstants from 'app/shared/constants/ValueConstants';
import appUtils from 'app/shared/utils/appUtils';
import IconX from 'images/icons/x.svg';
import IconXWhiteBold from 'images/icons/x-white-bold.svg';
import * as S from './styles';

type ModalSize = 'auto' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
type PaddingSize = '0' | 'xs' | '1x' | '2x' | '3x' | '4x' | '6x' | '8x';
type PaddingInlineSize = 'xs' | '1x' | '2x' | '3x' | '4x' | '6x' | '8x';
type ModalPosition = 'top';

// Define type for ARIA attributes
interface AriaAttributes {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-modal'?: boolean;
}

// Define type for overlay props
interface OverlayProps extends AriaAttributes {
  role: 'dialog';
  onClick: () => void;
  darkOverlay?: boolean;
}

export interface PopupModalProps {
  /**
   * Modal component
   */
  children: React.ReactNode;

  /**
   * Custom close icon (default is dark X)
   */
  customCloseIcon?: React.ReactNode;

  /**
   * Uses dark gray transparent overlay instead
   */
  darkOverlay?: boolean;

  /**
   * Specifies that modal should be full height of screen
   */
  fullHeight?: boolean;

  /**
   * Floats close button on overlay instead of modal
   */
  floatingCloseButton?: boolean;

  /**
   * Flag to remove overlay
   */
  isFullScreen?: boolean;

  /**
   * Flag to disable ESC key functionality in webview environments
   */
  isWebview?: boolean;

  /**
   * ID of HTML element to append portal to
   */
  modalId?: string;

  /**
   * Required handler to hide modal
   */
  onHidePopup: () => void;

  /**
   * String to set padding-bottom
   */
  paddingBottom?: PaddingSize;

  /**
   * String to set padding-inline
   */
  paddingInline?: PaddingInlineSize;

  /**
   * String to set padding-top
   */
  paddingTop?: PaddingSize;

  /**
   * String to set position of modal (default middle of screen)
   */
  position?: ModalPosition;

  /**
   * Override for components that have their own scroll container
   */
  scrollContainerOverride?: boolean;

  /**
   * String to set padding-inline for scrollable content
   */
  scrollablePaddingInline?: PaddingInlineSize;

  /**
   * Specifies size of modal
   */
  size?: ModalSize;

  /**
   * Flag to animate modal to slide up from bottom
   */
  slideUp?: boolean;

  /**
   * Flag to show close button
   */
  useCloseButton?: boolean;

  /**
   * Additional class name
   */
  className?: string;

  /**
   * Modal label for accessibility (required for screen readers)
   */
  ariaLabel?: string;

  /**
   * ID of the element that describes the modal for accessibility
   */
  ariaDescribedBy?: string;

  /**
   * ID of the element that labels the modal for accessibility
   */
  ariaLabelledBy?: string;

  /**
   * Disable body scroll locking on mobile
   */
  disableMobileScrollLock?: boolean;
}

/**
 * PopupModal - the one modal to rule them all
 * This component is used to wrap components to be displayed over content in a modal window.
 */
const PopupModal: React.FC<PopupModalProps> = ({
  ariaDescribedBy,
  ariaLabel,
  ariaLabelledBy,
  children,
  className,
  customCloseIcon,
  darkOverlay = false,
  floatingCloseButton = false,
  fullHeight = false,
  isFullScreen,
  isWebview = false,
  modalId = 'modal-root',
  onHidePopup,
  paddingBottom = '4x',
  paddingInline,
  paddingTop = '6x',
  position,
  scrollablePaddingInline,
  scrollContainerOverride,
  size = 'auto',
  slideUp = false,
  useCloseButton = true,
  disableMobileScrollLock = false,
  ...additionalProps
}) => {
  // Redux
  const isMobile = useSelector((state: any) => state.app.device.screenWidth === 'sm');

  // React refs
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle ESC key press to close the modal
  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === ValueConstants.ESC_KEY && !isWebview) {
        onHidePopup();
      }
    },
    [onHidePopup, isWebview],
  );

  // Set up event listeners and handle body scrolling
  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);

    // Lock scrolling on mobile devices (unless disabled)
    if (isMobile && !disableMobileScrollLock) {
      appUtils.lockVerticalScrolling({ isForPortal: true });
    }

    // Announce to screen readers that a modal has opened
    if (typeof window !== 'undefined' && 'screen' in window) {
      const screenReaderAlert = document.createElement('div');
      screenReaderAlert.setAttribute('role', 'status');
      screenReaderAlert.setAttribute('aria-live', 'polite');
      screenReaderAlert.className = 'sr-only';
      screenReaderAlert.textContent = 'Modal opened';
      document.body.appendChild(screenReaderAlert);

      // Clean up
      return () => {
        document.body.removeChild(screenReaderAlert);
        window.removeEventListener('keydown', handleKeydown);
        if (isMobile && !disableMobileScrollLock) {
          appUtils.unlockVerticalScrolling();
        }
      };
    }

    return () => {
      window.removeEventListener('keydown', handleKeydown);
      if (isMobile && !disableMobileScrollLock) {
        appUtils.unlockVerticalScrolling();
      }
    };
  }, [handleKeydown, isMobile, disableMobileScrollLock]);

  // Focus the close button initially
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  }, []);

  // Stop overlay's onClick from closing modal when clicking on content
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Add custom CSS class if contentHasPadding is false
  const contentClassName = className ?? '';

  // Filter out props that aren't needed or might cause conflicts
  const rest = omit(additionalProps, [
    'children',
    'customCloseIcon',
    'className',
    'dispatch',
    'isMobile',
    'modalId',
    'onHidePopup',
    'paddingBottom',
    'paddingInline',
    'paddingTop',
    'position',
    'scrollablePaddingInline',
    'slideUp',
    'useCloseButton',
    'ariaLabel',
    'ariaDescribedBy',
    'ariaLabelledBy',
    'isWebview',
    'disableMobileScrollLock',
  ]);

  // Create the props objects for each component with proper typing
  const overlayProps: OverlayProps = {
    role: 'dialog',
    'aria-modal': true,
    onClick: onHidePopup,
    darkOverlay: darkOverlay ? true : undefined,
  };

  // Add conditional ARIA attributes
  if (ariaLabel) {
    overlayProps['aria-label'] = ariaLabel;
  }

  if (ariaLabelledBy) {
    overlayProps['aria-labelledby'] = ariaLabelledBy;
  }

  if (ariaDescribedBy) {
    overlayProps['aria-describedby'] = ariaDescribedBy;
  }

  const contentProps = {
    ref: contentRef,
    className,
    position,
    slideUp: slideUp ? true : undefined,
    isFullScreen: isFullScreen ? true : undefined,
    paddingTop: !useCloseButton ? '2x' : paddingTop,
    paddingBottom: !useCloseButton ? '1x' : paddingBottom,
    paddingInline,
    fullHeight: fullHeight ? true : undefined,
    size,
    floatingCloseButton: floatingCloseButton ? true : undefined,
    onClick: handleModalClick,
    tabIndex: -1, // Make div focusable but not in tab order
    ...rest,
  };

  const closeButtonProps = {
    ref: buttonRef,
    isFullScreen: isFullScreen ? true : undefined,
    floatingCloseButton: floatingCloseButton ? true : undefined,
    scrollablePaddingInline: !floatingCloseButton ? scrollablePaddingInline : undefined,
    onClick: onHidePopup,
    'aria-label': 'Close modal',
  };

  const Content = (
    // FocusLock locks tab focus to the modal and prevents events behind the modal from being triggered
    <FocusLock autoFocus={false} returnFocus>
      <S.PopupModalOverlay {...overlayProps}>
        <S.PopupModalContent {...contentProps}>
          {useCloseButton && (
            <S.PopupModalCloseButton {...closeButtonProps}>
              {customCloseIcon ? (
                customCloseIcon
              ) : (
                <img
                  src={darkOverlay && floatingCloseButton ? IconXWhiteBold : IconX}
                  height={darkOverlay ? '14px' : '16px'}
                  width={darkOverlay ? '14px' : '16px'}
                  alt=""
                />
              )}
            </S.PopupModalCloseButton>
          )}
          {scrollContainerOverride ? (
            <>{children}</>
          ) : (
            <S.ScrollableContent
              id="modal-scroll-container"
              paddingBottom={paddingBottom}
              paddingInline={scrollablePaddingInline}
            >
              {children}
            </S.ScrollableContent>
          )}
        </S.PopupModalContent>
      </S.PopupModalOverlay>
    </FocusLock>
  );

  /**
   * Renders content inline on SSR, then renders content in a portal `modal-root` once the client
   * and document is loaded. This will allow us to have content still appear in HTML on SSR, which
   * is important if we use modals for SSR critical content, like HDPs
   */
  if (typeof document !== 'undefined') {
    const modalElement = document.getElementById(modalId);
    return modalElement ? ReactDOM.createPortal(Content, modalElement) : null;
  } else {
    return Content;
  }
};

export default PopupModal;
